import { BlockId } from '../../game/defs/block';
import { BlockDef, GridDef } from '../../game/defs/map';
import {
  ChainBlockLayout,
  ChainBlockLayoutType,
  ChainBlockView,
  isLineLayout,
} from '../../game/views/blocks/ChainBlockView';
import {
  PondBlockLayout,
  PondBlockLayoutType,
  PondBlockView,
} from '../../game/views/blocks/PondBlockView';
import { Container } from '../../odie';

export function applyEditorCellLayout(
  grid: GridDef,
  block: BlockDef,
  view: Container,
  column: number,
  row: number,
) {
  switch (block.id) {
    case 'chain':
      applyChainLayout(grid, view as ChainBlockView, column, row);
      break;
    case 'pond':
      applyPondLayout(grid, view as PondBlockView, column, row);
      break;
  }
}

function getNearestNeighbours(
  grid: GridDef,
  id: BlockId,
  column: number,
  row: number,
) {
  const top = grid.cells[column]?.[row - 1]?.blocks?.find(
    (block) => block.id === id,
  );
  const right = grid.cells[column + 1]?.[row]?.blocks?.find(
    (block) => block.id === id,
  );
  const bottom = grid.cells[column]?.[row + 1]?.blocks?.find(
    (block) => block.id === id,
  );
  const left = grid.cells[column - 1]?.[row]?.blocks?.find(
    (block) => block.id === id,
  );

  return [top, right, bottom, left];
}

function applyChainLayout(
  grid: GridDef,
  view: ChainBlockView,
  column: number,
  row: number,
) {
  const neighbor = getNearestNeighbours(grid, 'chain', column, row);
  const [top, right, bottom, left] = neighbor;
  const numNeighbors = neighbor.reduce(
    (acc, entity) => acc + (entity ? 1 : 0),
    0,
  );

  let layout: ChainBlockLayoutType = ChainBlockLayout.Horizontal;

  if (numNeighbors === 1) {
    // Puzzle design shouldn't allow it, throw Error instead?
    if (top || bottom) {
      layout = ChainBlockLayout.Vertical;
    } else if (left || right) {
      layout = ChainBlockLayout.Horizontal;
    }
  } else if (numNeighbors === 2) {
    // Horizontal, Vertical or Corner block
    if (top && bottom) {
      layout = ChainBlockLayout.Vertical;
    } else if (left && right) {
      layout = ChainBlockLayout.Horizontal;
    } else if (left && top) {
      layout = ChainBlockLayout.CornerBottomRight;
    } else if (top && right) {
      layout = ChainBlockLayout.CornerBottomLeft;
    } else if (right && bottom) {
      layout = ChainBlockLayout.CornerTopLeft;
    } else if (bottom && left) {
      layout = ChainBlockLayout.CornerTopRight;
    }
  } else if (numNeighbors === 3) {
    // T Section block
    if (left && top && right) {
      layout = ChainBlockLayout.TSectionBottom;
    } else if (top && right && bottom) {
      layout = ChainBlockLayout.TSectionLeft;
    } else if (right && bottom && left) {
      layout = ChainBlockLayout.TSectionTop;
    } else if (bottom && left && top) {
      layout = ChainBlockLayout.TSectionRight;
    }
  } else if (numNeighbors === 4) {
    // Cross block
    layout = ChainBlockLayout.Cross;
  }

  if (isLineLayout(layout)) {
    const isHorizontal = layout === ChainBlockLayout.Horizontal;
    const gridIndex = isHorizontal ? column : row;

    // switch for alternate layouts on cells with odd map positions
    if (gridIndex % 2 !== 0) {
      layout = isHorizontal
        ? ChainBlockLayout.HorizontalAlternate
        : ChainBlockLayout.VerticalAlternate;
    }
  }

  view.layout = layout;
}

function applyPondLayout(
  grid: GridDef,
  view: PondBlockView,
  column: number,
  row: number,
) {
  const neighbor = getNearestNeighbours(grid, 'pond', column, row);
  const [top, right, bottom, left] = neighbor;
  const numNeighbors = neighbor.reduce(
    (acc, entity) => acc + (entity ? 1 : 0),
    0,
  );

  let layout: PondBlockLayoutType = PondBlockLayout.Horizontal;

  if (numNeighbors === 0) {
    // Single block
    layout = PondBlockLayout.Single;
  } else if (numNeighbors === 1) {
    // End block
    if (top) {
      layout = PondBlockLayout.EndBottom;
    } else if (bottom) {
      layout = PondBlockLayout.EndTop;
    } else if (left) {
      layout = PondBlockLayout.EndRight;
    } else if (right) {
      layout = PondBlockLayout.EndLeft;
    }
  } else if (numNeighbors === 2) {
    // Horizontal, Vertical or Corner block
    if (top && bottom) {
      layout = PondBlockLayout.Vertical;
    } else if (left && right) {
      layout = PondBlockLayout.Horizontal;
    } else if (left && top) {
      layout = PondBlockLayout.CornerBottomRight;
    } else if (top && right) {
      layout = PondBlockLayout.CornerBottomLeft;
    } else if (right && bottom) {
      layout = PondBlockLayout.CornerTopLeft;
    } else if (bottom && left) {
      layout = PondBlockLayout.CornerTopRight;
    }
  } else if (numNeighbors === 3) {
    // T Section block
    if (left && top && right) {
      layout = PondBlockLayout.TSectionBottom;
    } else if (top && right && bottom) {
      layout = PondBlockLayout.TSectionLeft;
    } else if (right && bottom && left) {
      layout = PondBlockLayout.TSectionTop;
    } else if (bottom && left && top) {
      layout = PondBlockLayout.TSectionRight;
    }
  } else if (numNeighbors === 4) {
    // Cross block
    layout = PondBlockLayout.Cross;
  }

  view.layout = layout;
}
